/* eslint-disable react-hooks/exhaustive-deps */
import { FormikProvider } from 'formik'
import {
  Camera,
  Check,
  ChevronLeft,
  ChevronRight,
  ImageUp,
  Loader2,
  QrCode,
  RefreshCw,
} from 'lucide-react'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import { ProjectContext } from '../../../App'
import { useRentForm } from '../../../hooks/useRentForm'
import { RentalAssetSummary, RentalChargePreview } from '../../../types/rental.type'
import { currencyFormatter } from '../../../utils/formatter'
import { resolveAssetBillingPeriod } from '../../../utils/billingPeriod'
import {
  getReadablePrimaryButtonStyle,
  resolveThemeColor,
} from '../../../utils/buttonColor'
import { IDENTITY_ACCEPT, MAX_IDENTITY_MB } from '../../../utils/identityImage'
import { decodeQrFromImageFile } from '../../../utils/customerQr'

const STEP_DETAILS = 0
const STEP_ITEM = 1
const STEP_DURATION = 2
const STEP_IDENTITY = 3
const STEP_REVIEW = 4

const STEP_IDS = ['details', 'item', 'duration', 'identity', 'review'] as const

// Fields validated when leaving each step (the review step re-checks everything).
const STEP_FIELD_KEYS: string[][] = [
  ['customerName', 'customerPhoneNo', 'customerPhonePrefix', 'customerEmail', 'customerAddress'],
  ['assetId'],
  ['startDate', 'expectedReturnDate'],
  [],
]

function RequiredMark({ title }: { title: string }) {
  return (
    <span className="ml-0.5 text-red-500" aria-hidden="true" title={title}>
      *
    </span>
  )
}

function RentForm() {
  const {
    formik,
    groups,
    assets,
    isLoadingCatalog,
    selectedAsset,
    preview,
    isLoadingPreview,
    identity,
    identityError,
    pickIdentity,
    clearIdentity,
    isSubmitting,
    submitError,
    selectAsset,
    markReturnDateEdited,
    applyCustomerQr,
    qrStatus,
    setQrStatus,
  } = useRentForm()
  const { project } = useContext(ProjectContext)
  const { t, i18n } = useTranslation('rent')

  const cameraInputRef = useRef<HTMLInputElement>(null)
  const galleryInputRef = useRef<HTMLInputElement>(null)
  const qrInputRef = useRef<HTMLInputElement>(null)
  const stepContentRef = useRef<HTMLDivElement>(null)

  const [step, setStep] = useState(STEP_DETAILS)
  const [stepSummary, setStepSummary] = useState<{ name: string; label: string }[]>([])
  const [keyboardInset, setKeyboardInset] = useState(0)

  const STEPS = useMemo(
    () => STEP_IDS.map((id) => ({ id, label: t(`steps.${id}`) })),
    [t, i18n.language]
  )

  const FIELD_LABELS = useMemo(
    (): Record<string, string> => ({
      assetId: t('fields.item'),
      startDate: t('fields.startDate'),
      expectedReturnDate: t('fields.returnDate'),
      customerName: t('fields.fullName'),
      customerPhoneNo: t('fields.phoneNumber'),
      customerEmail: t('fields.email'),
      customerAddress: t('fields.address'),
    }),
    [t, i18n.language]
  )

  const assetsByGroup = useMemo(() => {
    // Rented-out items stay listed so customers know what exists, but they sink below the
    // ones they can actually pick.
    const pickableFirst = (list: RentalAssetSummary[]) =>
      [...list].sort((a, b) => Number(b.isAvailable) - Number(a.isAvailable))

    const named = groups.map((group) => ({
      groupId: group.groupId,
      name: group.name,
      assets: pickableFirst(assets.filter((asset) => asset.groupId === group.groupId)),
    }))
    // Items whose group is inactive or missing would otherwise vanish from the catalogue.
    const knownIds = new Set(groups.map((group) => group.groupId))
    const orphans = assets.filter((asset) => !knownIds.has(asset.groupId))
    if (orphans.length > 0) {
      named.push({ groupId: 'other', name: t('otherItems'), assets: pickableFirst(orphans) })
    }
    return named.filter((group) => group.assets.length > 0)
  }, [groups, assets, t, i18n.language])

  const summaryForStep = (stepIndex: number, errors: Record<string, unknown>) => {
    const keys = STEP_FIELD_KEYS[stepIndex] || []
    const summary: { name: string; label: string }[] = []
    keys.forEach((key) => {
      if (errors[key]) summary.push({ name: key, label: FIELD_LABELS[key] || key })
    })
    return summary
  }

  const markStepTouched = (stepIndex: number) => {
    const keys = STEP_FIELD_KEYS[stepIndex] || []
    const touched = keys.reduce((acc, key) => ({ ...acc, [key]: true }), {} as Record<string, boolean>)
    formik.setTouched({ ...formik.touched, ...touched }, false)
  }

  const goNext = async () => {
    const errors = (await formik.validateForm()) as Record<string, unknown>
    markStepTouched(step)

    const summary = summaryForStep(step, errors)
    if (summary.length > 0) {
      setStepSummary(summary)
      return
    }

    if (step === STEP_IDENTITY && !identity) {
      setStepSummary([{ name: 'identity', label: t('fields.identity') }])
      return
    }

    setStepSummary([])
    setStep((prev) => Math.min(prev + 1, STEP_REVIEW))
  }

  const handleFinalSubmit = async () => {
    const errors = (await formik.validateForm()) as Record<string, unknown>
    formik.setTouched(
      Object.keys(formik.values).reduce((acc, key) => ({ ...acc, [key]: true }), {} as Record<string, boolean>),
      false
    )

    for (let i = 0; i < STEP_FIELD_KEYS.length; i++) {
      const summary = summaryForStep(i, errors)
      if (summary.length > 0) {
        setStep(i)
        setStepSummary(summary)
        return
      }
    }

    if (!identity) {
      setStep(STEP_IDENTITY)
      setStepSummary([{ name: 'identity', label: t('fields.identity') }])
      return
    }

    setStepSummary([])
    formik.handleSubmit()
  }

  const handlePrimary = () => {
    if (step < STEP_REVIEW) {
      goNext()
    } else {
      handleFinalSubmit()
    }
  }

  const handleQrFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    // Reset immediately so re-picking the same file still fires a change event.
    event.target.value = ''
    if (!file) return

    setQrStatus({ state: 'loading', message: '' })
    try {
      const code = await decodeQrFromImageFile(file)
      if (!code) {
        setQrStatus({ state: 'error', message: t('qr.unreadable') })
        return
      }
      await applyCustomerQr(code)
    } catch (error) {
      console.error(error)
      setQrStatus({ state: 'error', message: t('qr.unreadable') })
    }
  }

  const handleIdentityPick = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    // Reset immediately so re-picking the same file still fires a change event.
    event.target.value = ''
    if (!file) return
    await pickIdentity(file)
  }

  // A conflict clears the chosen item, so the customer is put back in front of the
  // refreshed list instead of reviewing a request with a blank item.
  useEffect(() => {
    if (submitError && !formik.values.assetId) setStep(STEP_ITEM)
  }, [submitError, formik.values.assetId])

  // Drop entries from the "please complete" list as they are filled in, so the warning
  // shrinks away while the customer types instead of accusing them of a fixed field.
  useEffect(() => {
    setStepSummary((prev) => {
      if (prev.length === 0) return prev
      const errors = formik.errors as Record<string, unknown>
      const remaining = prev.filter((item) =>
        item.name === 'identity' ? !identity : Boolean(errors[item.name])
      )
      return remaining.length === prev.length ? prev : remaining
    })
  }, [formik.errors, identity])

  // Keep the fixed action bar glued to the bottom of the visual viewport (top of the
  // keyboard) on mobile, matching the booking form.
  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return
    const update = () => {
      setKeyboardInset(Math.round(Math.max(0, window.innerHeight - vv.height - vv.offsetTop)))
    }
    vv.addEventListener('resize', update)
    vv.addEventListener('scroll', update)
    update()
    return () => {
      vv.removeEventListener('resize', update)
      vv.removeEventListener('scroll', update)
    }
  }, [])

  useEffect(() => {
    const container = stepContentRef.current
    if (!container) return
    const firstSection = container.querySelector<HTMLElement>('section')
    const focusable = firstSection?.querySelector<HTMLElement>(
      'input:not([disabled]), textarea:not([disabled]), select:not([disabled])'
    )
    focusable?.focus()
  }, [step])

  const themeColor = resolveThemeColor(project?.themeColor)
  const buttonColor = resolveThemeColor(project?.buttonColor)
  const fieldClass =
    'mt-1.5 w-full rounded-xl border border-gray-200 bg-white px-3.5 py-3 text-sm font-medium text-gray-900 shadow-sm transition-colors placeholder:text-gray-400 focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-slate-300/80'
  const labelClass = 'block text-sm font-medium text-gray-700'
  const sectionIntroClass = 'text-xs font-semibold uppercase tracking-wider text-gray-500'
  const errorTextClass = 'mt-1.5 text-sm text-red-600'
  const sectionCardClass = 'rounded-2xl border border-gray-200/70 bg-white p-5 shadow-sm shadow-gray-900/5 sm:p-6'

  const locale = i18n.language?.startsWith('id') ? 'id' : 'en'
  const dateLabel = (value: string) =>
    value ? moment(value, 'YYYY-MM-DD').locale(locale).format('dddd, DD MMMM YYYY') : '-'
  const requiredTitle = t('required')

  /** The period an item bills in: "day", or "2 weeks" where the interval is more than one. */
  const perLabel = (asset: RentalAssetSummary) => {
    const { interval, unit } = resolveAssetBillingPeriod(asset)
    return interval > 1 ? t(`per.${unit}Plural`, { count: interval }) : t(`per.${unit}`)
  }

  const rateLabel = (asset: RentalAssetSummary) =>
    `${currencyFormatter(asset.defaultRate)} / ${perLabel(asset)}`

  /**
   * Billing periods are collapsed into plain units for the customer: "3 x 2 weeks" is how
   * the server bills, but "6 weeks" is what they asked for.
   */
  const chargeDurationLabel = (charge: RentalChargePreview) => {
    const count = charge.duration * (charge.interval || 1)
    const unit = t(`unit.${charge.durationUnit}${count === 1 ? 'One' : ''}`)
    return t('chargedFor', { count, unit })
  }

  return (
    <div
      id="rent-form"
      className="overflow-hidden rounded-2xl border border-gray-200/80 bg-white text-left text-gray-900 shadow-sm shadow-gray-900/5"
    >
      <header
        className="border-b border-gray-100 px-5 py-4 sm:px-8 sm:py-5"
        style={{
          background: `linear-gradient(135deg, ${themeColor}08 0%, transparent 55%), linear-gradient(to bottom, #fafafa, #ffffff)`,
        }}
      >
        <Stepper step={step} themeColor={themeColor} steps={STEPS} />
      </header>

      <div className="px-5 py-5 sm:px-8 sm:py-6" ref={stepContentRef}>
        <FormikProvider value={formik}>
          <form
            className="flex flex-col gap-5 max-sm:pb-24"
            action="#!"
            onSubmit={(e) => {
              e.preventDefault()
              handlePrimary()
              return false
            }}
          >
            {step === STEP_DETAILS && (
              <section className={sectionCardClass}>
                <p className={sectionIntroClass}>{t('steps.details')}</p>

                <div className="mt-4 rounded-xl border border-dashed border-gray-300 bg-gray-50/60 p-4">
                  <p className="text-sm font-medium text-gray-700">{t('qr.title')}</p>
                  <p className="mt-1 text-xs text-gray-500">{t('qr.hint')}</p>
                  <input
                    ref={qrInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleQrFileChange}
                  />
                  <button
                    type="button"
                    className="mt-3 inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-60"
                    disabled={qrStatus.state === 'loading'}
                    onClick={() => qrInputRef.current?.click()}
                  >
                    {qrStatus.state === 'loading' ? (
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                    ) : (
                      <QrCode className="h-4 w-4" aria-hidden />
                    )}
                    {t('qr.upload')}
                  </button>
                  {qrStatus.message ? (
                    <p
                      className={
                        qrStatus.state === 'error' ? errorTextClass : 'mt-1.5 text-sm text-green-600'
                      }
                    >
                      {qrStatus.message}
                    </p>
                  ) : null}
                </div>

                <div className="mt-4 space-y-4">
                  <div>
                    <label className={labelClass} htmlFor="rent-customer-name">
                      {t('fields.fullName')} <RequiredMark title={requiredTitle} />
                    </label>
                    <input
                      id="rent-customer-name"
                      type="text"
                      name="customerName"
                      autoComplete="name"
                      value={formik.values.customerName}
                      className={fieldClass}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.customerName && formik.errors.customerName ? (
                      <p className={errorTextClass}>{String(formik.errors.customerName)}</p>
                    ) : null}
                  </div>

                  <div>
                    <span className={labelClass}>
                      {t('fields.phoneNumber')} <RequiredMark title={requiredTitle} />
                    </span>
                    <div className="mt-1.5 flex gap-2">
                      <select
                        className={`${fieldClass} !w-20 shrink-0 !px-2`}
                        name="customerPhonePrefix"
                        value={formik.values.customerPhonePrefix}
                        onChange={formik.handleChange}
                      >
                        <option value="+62">+62</option>
                      </select>
                      <input
                        type="tel"
                        name="customerPhoneNo"
                        inputMode="tel"
                        autoComplete="tel-national"
                        value={formik.values.customerPhoneNo}
                        className={`${fieldClass} min-w-0 flex-1`}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                    </div>
                    {formik.touched.customerPhoneNo && formik.errors.customerPhoneNo ? (
                      <p className={errorTextClass}>{String(formik.errors.customerPhoneNo)}</p>
                    ) : null}
                  </div>

                  <div>
                    <label className={labelClass} htmlFor="rent-customer-address">
                      {t('fields.address')} <RequiredMark title={requiredTitle} />
                    </label>
                    <textarea
                      id="rent-customer-address"
                      name="customerAddress"
                      rows={3}
                      autoComplete="street-address"
                      value={formik.values.customerAddress}
                      className={`${fieldClass} min-h-[5.5rem] resize-y`}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    <p className="mt-1.5 text-xs text-gray-500">{t('addressHint')}</p>
                    {formik.touched.customerAddress && formik.errors.customerAddress ? (
                      <p className={errorTextClass}>{String(formik.errors.customerAddress)}</p>
                    ) : null}
                  </div>

                  <div>
                    <label className={labelClass} htmlFor="rent-customer-email">
                      {t('fields.email')}{' '}
                      <span className="font-normal text-gray-500">{t('optional')}</span>
                    </label>
                    <input
                      id="rent-customer-email"
                      type="email"
                      name="customerEmail"
                      autoComplete="email"
                      value={formik.values.customerEmail}
                      className={fieldClass}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.customerEmail && formik.errors.customerEmail ? (
                      <p className={errorTextClass}>{String(formik.errors.customerEmail)}</p>
                    ) : null}
                  </div>
                </div>
              </section>
            )}

            {step === STEP_ITEM && (
              <section className={sectionCardClass} id="rent-items">
                <p className={sectionIntroClass}>
                  {t('steps.item')} <RequiredMark title={requiredTitle} />
                </p>

                {isLoadingCatalog ? (
                  <div className="mt-6 flex justify-center py-6">
                    <Loader2 className="h-5 w-5 animate-spin text-gray-400" aria-hidden />
                  </div>
                ) : assetsByGroup.length === 0 ? (
                  <p className="mt-4 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-500">
                    {t('noItems')}
                  </p>
                ) : (
                  <div className="mt-4 space-y-6">
                    {assetsByGroup.map((group) => (
                      <div key={group.groupId}>
                        <p className="text-sm font-semibold text-gray-900">{group.name}</p>
                        <ul className="mt-2.5 list-none space-y-2.5 p-0">
                          {group.assets.map((asset) => {
                            const isSelected = formik.values.assetId === asset.assetId
                            return (
                              <li key={asset.assetId}>
                                <button
                                  type="button"
                                  disabled={!asset.isAvailable}
                                  aria-pressed={isSelected}
                                  className={`flex w-full items-start justify-between gap-3 rounded-xl border p-3.5 text-left transition ${
                                    asset.isAvailable
                                      ? 'border-gray-200 bg-white hover:bg-gray-50'
                                      : 'cursor-not-allowed border-gray-200/70 bg-gray-50 opacity-60'
                                  }`}
                                  style={
                                    isSelected
                                      ? { borderColor: themeColor, backgroundColor: `${themeColor}0d` }
                                      : undefined
                                  }
                                  onClick={() => selectAsset(asset.assetId)}
                                >
                                  <span className="min-w-0 flex-1">
                                    <span className="block text-sm font-semibold text-gray-900">
                                      {asset.name}
                                    </span>
                                    {asset.description ? (
                                      <span className="mt-0.5 block text-xs text-gray-500">
                                        {asset.description}
                                      </span>
                                    ) : null}
                                    <span className="mt-1.5 block text-xs font-medium text-gray-700">
                                      {rateLabel(asset)}
                                    </span>
                                    {asset.depositAmount > 0 ? (
                                      <span className="mt-0.5 block text-xs text-gray-500">
                                        {t('depositOf', { amount: currencyFormatter(asset.depositAmount) })}
                                      </span>
                                    ) : null}
                                  </span>
                                  <span className="shrink-0">
                                    {asset.isAvailable ? (
                                      isSelected ? (
                                        <Check className="h-5 w-5" style={{ color: themeColor }} aria-hidden />
                                      ) : null
                                    ) : (
                                      <span className="rounded-full bg-gray-200 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-gray-600">
                                        {t('unavailable')}
                                      </span>
                                    )}
                                  </span>
                                </button>
                              </li>
                            )
                          })}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}

                {formik.touched.assetId && formik.errors.assetId ? (
                  <p className={errorTextClass}>{String(formik.errors.assetId)}</p>
                ) : null}
              </section>
            )}

            {step === STEP_DURATION && (
              <>
                <section className={sectionCardClass}>
                  <p className={sectionIntroClass}>{t('steps.duration')}</p>
                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className={labelClass} htmlFor="rent-start-date">
                        {t('fields.startDate')} <RequiredMark title={requiredTitle} />
                      </label>
                      <input
                        id="rent-start-date"
                        type="date"
                        name="startDate"
                        min={moment().format('YYYY-MM-DD')}
                        value={formik.values.startDate}
                        className={fieldClass}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      {formik.touched.startDate && formik.errors.startDate ? (
                        <p className={errorTextClass}>{String(formik.errors.startDate)}</p>
                      ) : null}
                    </div>
                    <div>
                      <label className={labelClass} htmlFor="rent-return-date">
                        {t('fields.returnDate')} <RequiredMark title={requiredTitle} />
                      </label>
                      <input
                        id="rent-return-date"
                        type="date"
                        name="expectedReturnDate"
                        min={moment(formik.values.startDate || undefined)
                          .add(1, 'day')
                          .format('YYYY-MM-DD')}
                        value={formik.values.expectedReturnDate}
                        className={fieldClass}
                        onChange={(event) => {
                          markReturnDateEdited()
                          formik.handleChange(event)
                        }}
                        onBlur={formik.handleBlur}
                      />
                      {formik.touched.expectedReturnDate && formik.errors.expectedReturnDate ? (
                        <p className={errorTextClass}>{String(formik.errors.expectedReturnDate)}</p>
                      ) : null}
                    </div>
                  </div>
                  {selectedAsset ? (
                    <p className="mt-3 text-xs text-gray-500">
                      {t('billedPer', { per: perLabel(selectedAsset) })}
                    </p>
                  ) : null}
                </section>

                <section className={sectionCardClass}>
                  <p className={sectionIntroClass}>{t('estimate')}</p>
                  {isLoadingPreview ? (
                    <div className="mt-4 flex justify-center py-3">
                      <Loader2 className="h-5 w-5 animate-spin text-gray-400" aria-hidden />
                    </div>
                  ) : preview ? (
                    <>
                      <dl className="mt-4 space-y-2.5 text-sm">
                        <div className="flex items-center justify-between gap-3">
                          <dt className="text-gray-600">{chargeDurationLabel(preview)}</dt>
                          <dd className="font-semibold text-gray-900">
                            {currencyFormatter(preview.rentalAmount)}
                          </dd>
                        </div>
                        {preview.depositAmount > 0 ? (
                          <div className="flex items-center justify-between gap-3">
                            <dt className="text-gray-600">{t('fields.deposit')}</dt>
                            <dd className="font-semibold text-gray-900">
                              {currencyFormatter(preview.depositAmount)}
                            </dd>
                          </div>
                        ) : null}
                        <div className="flex items-center justify-between gap-3 border-t border-gray-100 pt-2.5">
                          <dt className="font-semibold text-gray-900">{t('fields.total')}</dt>
                          <dd className="text-base font-semibold" style={{ color: themeColor }}>
                            {currencyFormatter(preview.totalAmount)}
                          </dd>
                        </div>
                      </dl>
                      <p className="mt-3 text-xs text-gray-500">{t('estimateHint')}</p>
                    </>
                  ) : (
                    <p className="mt-4 text-sm text-gray-500">{t('estimateUnavailable')}</p>
                  )}
                </section>
              </>
            )}

            {step === STEP_IDENTITY && (
              <section className={sectionCardClass}>
                <p className={sectionIntroClass}>
                  {t('steps.identity')} <RequiredMark title={requiredTitle} />
                </p>
                <p className="mt-2 text-sm text-gray-600">{t('identity.hint')}</p>

                <input
                  ref={cameraInputRef}
                  type="file"
                  accept={IDENTITY_ACCEPT}
                  capture="environment"
                  className="hidden"
                  onChange={handleIdentityPick}
                />
                <input
                  ref={galleryInputRef}
                  type="file"
                  accept={IDENTITY_ACCEPT}
                  className="hidden"
                  onChange={handleIdentityPick}
                />

                {identity ? (
                  <div className="mt-4">
                    <img
                      src={identity.dataUrl}
                      alt={t('identity.previewAlt')}
                      className="max-h-64 w-full rounded-xl border border-gray-200 object-contain"
                    />
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                        onClick={() => cameraInputRef.current?.click()}
                      >
                        <RefreshCw className="h-4 w-4" aria-hidden />
                        {t('identity.retake')}
                      </button>
                      <button
                        type="button"
                        className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                        onClick={() => galleryInputRef.current?.click()}
                      >
                        <ImageUp className="h-4 w-4" aria-hidden />
                        {t('identity.replace')}
                      </button>
                      <button
                        type="button"
                        className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-500 transition hover:text-red-600"
                        onClick={clearIdentity}
                      >
                        {t('identity.remove')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-4 grid gap-2.5 sm:grid-cols-2">
                    <button
                      type="button"
                      className="inline-flex items-center justify-center gap-2 rounded-xl py-3.5 text-sm font-semibold shadow-md transition hover:brightness-105 active:brightness-95"
                      style={getReadablePrimaryButtonStyle(buttonColor)}
                      onClick={() => cameraInputRef.current?.click()}
                    >
                      <Camera className="h-4 w-4" aria-hidden />
                      {t('identity.takePhoto')}
                    </button>
                    <button
                      type="button"
                      className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-300 bg-white py-3.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
                      onClick={() => galleryInputRef.current?.click()}
                    >
                      <ImageUp className="h-4 w-4" aria-hidden />
                      {t('identity.uploadImage')}
                    </button>
                  </div>
                )}

                <p className="mt-3 text-xs text-gray-500">
                  {t('identity.limit', { max: MAX_IDENTITY_MB })}
                </p>
                {identityError ? <p className={errorTextClass}>{identityError}</p> : null}
              </section>
            )}

            {step === STEP_REVIEW && (
              <section className={sectionCardClass}>
                <p className={sectionIntroClass}>{t('reviewYourRequest')}</p>
                <dl className="mt-4 space-y-4 text-sm">
                  <ReviewRow label={t('fields.item')}>{selectedAsset?.name || '-'}</ReviewRow>
                  <ReviewRow label={t('fields.startDate')}>{dateLabel(formik.values.startDate)}</ReviewRow>
                  <ReviewRow label={t('fields.returnDate')}>
                    {dateLabel(formik.values.expectedReturnDate)}
                  </ReviewRow>
                  {preview ? (
                    <ReviewRow label={t('fields.total')}>
                      <span className="font-semibold">{currencyFormatter(preview.totalAmount)}</span>
                      {preview.depositAmount > 0 ? (
                        <span className="mt-0.5 block text-xs text-gray-500">
                          {t('includesDeposit', { amount: currencyFormatter(preview.depositAmount) })}
                        </span>
                      ) : null}
                    </ReviewRow>
                  ) : null}
                  <ReviewRow label={t('fields.name')}>{formik.values.customerName || '-'}</ReviewRow>
                  <ReviewRow label={t('fields.phone')}>
                    {formik.values.customerPhoneNo
                      ? `${formik.values.customerPhonePrefix} ${formik.values.customerPhoneNo}`
                      : '-'}
                  </ReviewRow>
                  {formik.values.customerEmail ? (
                    <ReviewRow label={t('fields.email')}>{formik.values.customerEmail}</ReviewRow>
                  ) : null}
                  <ReviewRow label={t('fields.address')}>{formik.values.customerAddress || '-'}</ReviewRow>
                  <ReviewRow label={t('fields.identity')}>
                    {identity ? (
                      <img
                        src={identity.dataUrl}
                        alt={t('identity.previewAlt')}
                        className="mt-1 max-h-32 rounded-lg border border-gray-200 object-contain"
                      />
                    ) : (
                      '-'
                    )}
                  </ReviewRow>
                </dl>

                <div className="mt-5">
                  <label className={labelClass} htmlFor="rent-notes">
                    {t('fields.notes')}{' '}
                    <span className="font-normal text-gray-500">{t('optional')}</span>
                  </label>
                  <textarea
                    id="rent-notes"
                    name="notes"
                    rows={3}
                    value={formik.values.notes}
                    className={`${fieldClass} min-h-[5.5rem] resize-y`}
                    onChange={formik.handleChange}
                  />
                </div>

                <p className="mt-4 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-xs text-gray-600">
                  {t('requestNotice')}
                </p>
              </section>
            )}

            {submitError ? (
              <div
                role="alert"
                className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
              >
                {submitError}
              </div>
            ) : null}

            {stepSummary.length > 0 ? (
              <div
                role="alert"
                className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
              >
                <p className="font-semibold">
                  {stepSummary.length === 1
                    ? t('pleaseCompleteOne')
                    : t('pleaseCompleteMany', { count: stepSummary.length })}
                </p>
                <ul className="mt-1.5 list-disc space-y-0.5 pl-5">
                  {stepSummary.map((item) => (
                    <li key={item.name}>{item.label}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            <div
              className="z-20 flex items-center justify-between gap-3 border-t border-gray-100 bg-white/95 px-5 py-4 backdrop-blur max-sm:fixed max-sm:inset-x-0 sm:sticky sm:-mx-8 sm:px-8"
              style={{
                bottom: keyboardInset,
                paddingBottom: keyboardInset > 0 ? '1rem' : 'calc(env(safe-area-inset-bottom) + 1rem)',
              }}
            >
              {step > STEP_DETAILS ? (
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
                  onClick={() => {
                    setStepSummary([])
                    setStep(step - 1)
                  }}
                >
                  <ChevronLeft className="h-4 w-4" aria-hidden />
                  <span>{t('back')}</span>
                </button>
              ) : (
                <span />
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                aria-busy={isSubmitting}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl py-3.5 text-sm font-semibold shadow-md transition hover:brightness-105 active:brightness-95 disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:brightness-100 sm:flex-none sm:px-8"
                style={getReadablePrimaryButtonStyle(buttonColor)}
              >
                {step < STEP_REVIEW ? (
                  <>
                    <span>{t('continue')}</span>
                    <ChevronRight className="h-4 w-4" aria-hidden />
                  </>
                ) : isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                    <span>{t('submitting')}</span>
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4" aria-hidden />
                    <span>{t('submitRequest')}</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </FormikProvider>
      </div>
    </div>
  )
}

function Stepper({
  step,
  themeColor,
  steps,
}: {
  step: number
  themeColor: string
  steps: { id: string; label: string }[]
}) {
  const total = steps.length
  const currentLabel = steps[step]?.label ?? ''
  const progress = Math.min(((step + 1) / total) * 100, 100)
  const accent = getReadablePrimaryButtonStyle(themeColor).backgroundColor
  return (
    <div className="mt-4">
      <span className="block text-sm font-semibold text-gray-900">{currentLabel}</span>
      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
        <div
          className="h-full rounded-full transition-all duration-300 ease-out"
          style={{ width: `${progress}%`, backgroundColor: accent }}
          role="progressbar"
          aria-valuemin={1}
          aria-valuemax={total}
          aria-valuenow={step + 1}
        />
      </div>
    </div>
  )
}

function ReviewRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-gray-100 pb-3 last:border-b-0 last:pb-0 sm:flex-row sm:gap-4">
      <dt className="w-32 shrink-0 text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="min-w-0 flex-1 text-gray-900 [overflow-wrap:anywhere]">{children}</dd>
    </div>
  )
}

export default RentForm
